/**
 * การแสดงผลวันที่/เวลา
 * - DB เก็บ UTC (timestamptz) → แปลงเป็น Asia/Bangkok ตอนแสดงผลเท่านั้น
 * - ค.ศ. เป็นค่าเริ่มต้น (มีผู้เข้าอบรมต่างชาติ) สลับเป็น พ.ศ. ได้เมื่อออกรายงานภาษาไทย
 */

export const DISPLAY_TIME_ZONE = 'Asia/Bangkok';

export type EraOption = 'CE' | 'BE';

const BUDDHIST_ERA_OFFSET = 543;

export function toBuddhistYear(gregorianYear: number): number {
  return gregorianYear + BUDDHIST_ERA_OFFSET;
}

export function formatDate(
  value: Date | string,
  options: { locale?: string; era?: EraOption } = {},
): string {
  const { locale = 'th-TH', era = 'CE' } = options;
  const date = typeof value === 'string' ? new Date(value) : value;
  const formatted = new Intl.DateTimeFormat(locale, {
    timeZone: DISPLAY_TIME_ZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    calendar: 'gregory',
    numberingSystem: 'latn',
  }).format(date);

  if (era === 'CE') return formatted;

  const gregorianYear = Number(
    new Intl.DateTimeFormat('en-US', {
      timeZone: DISPLAY_TIME_ZONE,
      year: 'numeric',
      calendar: 'gregory',
    }).format(date),
  );
  return formatted.replace(String(gregorianYear), String(toBuddhistYear(gregorianYear)));
}

export function formatDateTime(
  value: Date | string,
  options: { locale?: string; era?: EraOption } = {},
): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  const time = new Intl.DateTimeFormat(options.locale ?? 'th-TH', {
    timeZone: DISPLAY_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    numberingSystem: 'latn',
  }).format(date);
  return `${formatDate(date, options)} ${time}`;
}

/** วินาที → mm:ss (ใช้กับนาฬิกาจับเวลาสอบ) */
export function formatDuration(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  const pad = (value: number): string => String(value).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
}

/** เปอร์เซ็นต์ — ทศนิยม 1 ตำแหน่ง ไม่ปัดจนบิดเบือน */
export function formatPercent(value: number | null | undefined, fractionDigits = 1): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${value.toFixed(fractionDigits)}%`;
}
